<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { DaikuanLoanRow } from '@/api/modules/daikuan'
import { fetchDaikuanRecycleLoans, restoreDaikuanLoan } from '@/api/modules/daikuan'
import { formatEpoch, formatMoneyInt } from '@/utils/format'

const q = reactive({ pageUi: 1, size: 10 })
const loading = ref(false)
const rows = ref<DaikuanLoanRow[]>([])
const total = ref(0)

async function load() {
  loading.value = true
  try {
    const { data } = await fetchDaikuanRecycleLoans({ page: q.pageUi, rows: q.size })
    rows.value = data.content ?? []
    total.value = data.totalElements ?? 0
  } finally {
    loading.value = false
  }
}

async function onRestore(row: DaikuanLoanRow) {
  try {
    await ElMessageBox.confirm(
      `恢复订单 ${row.orderNum ?? row.id} 到贷款列表？`,
      '恢复订单',
      { type: 'info' },
    )
    const { data } = await restoreDaikuanLoan(row.id)
    if (Number(data?.status) === 1) {
      ElMessage.success(data.msg ?? '已恢复')
      load()
    } else {
      ElMessage.error(String(data?.msg ?? '恢复失败'))
    }
  } catch (e) {
    if (e !== 'cancel') {
      /* */
    }
  }
}

function onPage() {
  load()
}

function onSize() {
  q.pageUi = 1
  load()
}

onMounted(load)
</script>

<template>
  <PageChrome
    title="订单回收站"
    description="软删除的信贷订单在此保留；恢复后重新出现在「信贷订单」列表，不影响用户、提现与还款数据。"
  >
    <el-card shadow="never" class="ep-card">
      <el-table v-loading="loading" :data="rows" stripe border style="width: 100%">
        <el-table-column prop="id" label="ID" width="72" align="center" />
        <el-table-column prop="orderNum" label="订单号" min-width="140" show-overflow-tooltip />
        <el-table-column prop="user" label="手机号" width="128" />
        <el-table-column label="金额" width="100" align="right">
          <template #default="{ row }">{{ formatMoneyInt(row.money) }}</template>
        </el-table-column>
        <el-table-column prop="months" label="期数" width="72" align="center" />
        <el-table-column label="创建时间" width="160" align="center">
          <template #default="{ row }">{{ formatEpoch(row.addtime) }}</template>
        </el-table-column>
        <el-table-column prop="statusDisplayEn" label="状态" min-width="120" />
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="onRestore(row)">恢复</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ep-pager-footer pager">
        <el-pagination
          v-model:current-page="q.pageUi"
          v-model:page-size="q.size"
          :total="total"
          :page-sizes="[10, 20, 50]"
          layout="sizes,prev,pager,next,total"
          background
          @current-change="onPage"
          @size-change="onSize"
        />
      </div>
    </el-card>
  </PageChrome>
</template>
